// Authenticated file helpers.
//
// Bare <a href="/api/..."> links send no bearer token (the API does not
// accept the session cookie), so a user clicking one lands on
// {"detail":"Not authenticated"}. Everything that opens or saves a file
// from a bearer-protected endpoint goes through here: fetch as a blob with
// the api client (which carries the token and refresh logic), then hand
// the browser an object URL.

import api from './api';
import { triggerBlobDownload } from '../utils/triggerDownload';

/** Backend document URLs are absolute ('/api/...'); the api client already prefixes '/api'. */
export function apiPath(url: string): string {
  return url.startsWith('/api/') ? url.slice(4) : url;
}

export async function fetchAuthedBlob(url: string): Promise<Blob> {
  const res = await api.get(url, { responseType: 'blob' });
  return res.data as Blob;
}

/** Open a bearer-protected file in a new tab (PDFs render inline). */
export async function openAuthedFile(url: string): Promise<void> {
  // Open the tab synchronously on the click so popup blockers allow it,
  // then point it at the blob once fetched.
  const tab = window.open('', '_blank');
  try {
    const blob = await fetchAuthedBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    if (tab) {
      tab.location.href = objectUrl;
    } else {
      window.location.assign(objectUrl);
    }
    // Give the tab time to load before the URL is revoked.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/** Save a bearer-protected file with the given filename. */
export async function downloadAuthedFile(url: string, filename: string): Promise<void> {
  const blob = await fetchAuthedBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  triggerBlobDownload(objectUrl, filename);
  URL.revokeObjectURL(objectUrl);
}
